/**
 * Sorting and filtering for the Time tables (UI standard 8: every column sortable and
 * filterable). The lists are one person's week, so this runs in the browser on loaded data.
 *
 * Merge seam: the row types below are structural subsets of GridRow (lib/time/grid) and
 * AssignableTask (schemas/time). The real types satisfy them as-is; once
 * Tasks 1-12 land they can be swapped for imports of those types.
 */

export type SortDir = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

export function nextSort<K extends string>(current: SortState<K>, key: K): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key, dir: "asc" };
}

const contains = (value: string | null | undefined, query: string) =>
  (value ?? "").toLowerCase().includes(query);

const direction = (dir: SortDir) => (dir === "asc" ? 1 : -1);

export interface GridRowLike {
  taskName: string;
  projectName: string;
  weekHours: number;
  cells: { hours: number }[];
}

export interface AssignableLike {
  projectTaskId: string;
  taskName: string;
  projectName: string;
  accountName?: string | null;
  projectId?: string | null;
  isFavorite: boolean;
  projectIsFavorite?: boolean;
}

export interface ProjectGroup<T extends AssignableLike> {
  /** The project id, else its name (an older gateway without ProjectID). */
  key: string;
  name: string;
  accountName: string | null;
  /** Null when the gateway gives no project id: no project star is offered. */
  projectId: string | null;
  projectIsFavorite: boolean;
  /** The project's loaded tasks, A-Z, starred or not. */
  tasks: T[];
  starred: number;
}

/**
 * The Favorites dialog's groups: one per project, every task kept under its project (starred or
 * not) so a person picks by project, then task. Projects with a star (theirs or a task's) come
 * first, then A-Z. A task listed twice by the server (two assignments) shows once.
 */
export function groupByProject<T extends AssignableLike>(items: T[]): ProjectGroup<T>[] {
  const seen = new Set<string>();
  const unique = items.filter((t) => !seen.has(t.projectTaskId) && seen.add(t.projectTaskId));
  const groups = new Map<string, ProjectGroup<T>>();
  for (const t of unique) {
    const key = t.projectId ? t.projectId.toUpperCase() : t.projectName;
    const group = groups.get(key) ?? {
      key,
      name: t.projectName,
      accountName: t.accountName ?? null,
      projectId: t.projectId ?? null,
      projectIsFavorite: false,
      tasks: [],
      starred: 0,
    };
    group.tasks.push(t);
    if (t.isFavorite) group.starred += 1;
    if (t.projectIsFavorite) group.projectIsFavorite = true;
    groups.set(key, group);
  }
  const marked = (g: ProjectGroup<T>) => (g.projectIsFavorite || g.starred > 0 ? 0 : 1);
  return [...groups.values()]
    .map((g) => ({
      ...g,
      tasks: [...g.tasks].sort((a, b) => a.taskName.localeCompare(b.taskName)),
    }))
    .sort((a, b) => marked(a) - marked(b) || a.name.localeCompare(b.name));
}

export type GridSortKey =
  | "task"
  | "week"
  | "day0"
  | "day1"
  | "day2"
  | "day3"
  | "day4"
  | "day5"
  | "day6";

export function filterGridRows<R extends GridRowLike>(rows: R[], query: string): R[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => contains(r.taskName, q) || contains(r.projectName, q));
}

function gridValue(row: GridRowLike, key: GridSortKey): number | string {
  if (key === "task") return row.taskName.toLowerCase();
  if (key === "week") return row.weekHours;
  return row.cells[Number(key.slice(3))]?.hours ?? 0;
}

export function sortGridRows<R extends GridRowLike>(rows: R[], sort: SortState<GridSortKey>): R[] {
  const d = direction(sort.dir);
  return [...rows].sort((a, b) => {
    const va = gridValue(a, sort.key);
    const vb = gridValue(b, sort.key);
    const primary = va < vb ? -1 : va > vb ? 1 : 0;
    return primary * d || a.taskName.localeCompare(b.taskName);
  });
}
